import { colors } from '@globals/themes/defaultTheme'
import { mobileQuery } from '@globals/themes/themeConstants'
import Close from '@mui/icons-material/Close'
import SearchIcon from '@mui/icons-material/Search'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import InputAdornment from '@mui/material/InputAdornment'
import Stack from '@mui/material/Stack'
import { SxProps } from '@mui/material/styles'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { Selectable } from '@shared/filters/selections'
import ErrorText from '@shared/forms/ErrorText'
import { RecognitionFormData } from '@tenant/recognition/RecognitionModal/sendRecognitionView/useSendRecognitionForm'
import { useT } from '@transifex/react'
import { useMemo, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'

const groupHeaderSx: SxProps = {
  color: colors.grayMedium,
  mt: 0,
  [mobileQuery]: {
    mt: 0,
  },
}

type MobileRecognitionQualitiesViewProps = {
  options?: Selectable[]
}

const MobileRecognitionQualitiesView: React.FC<MobileRecognitionQualitiesViewProps> = ({
  options,
}) => {
  const t = useT()
  const { control, setValue, formState } = useFormContext<RecognitionFormData>()
  const qualitiesError = formState.errors.qualities
  const selectedQualities = useWatch({ control, name: 'qualities' }) as Selectable[] | undefined
  const selectedIds = useMemo(
    () => new Set((selectedQualities ?? []).map(q => q.id)),
    [selectedQualities],
  )
  const [searchText, setSearchText] = useState('')

  const handleQualityToggle = (quality: Selectable, checked: boolean) => {
    const current = selectedQualities ?? []
    const next = checked ? [...current, quality] : current.filter(q => q.id !== quality.id)
    setValue('qualities', next, { shouldValidate: true })
  }

  const filteredBySearch = useMemo(
    () =>
      (options ?? []).filter(option =>
        option.name.toLowerCase().includes(searchText.trim().toLowerCase()),
      ),
    [options, searchText],
  )

  const groupedByValue = useMemo(() => {
    const groups = new Map<string, Selectable[]>()
    for (const option of filteredBySearch) {
      const group = option.tooltipValue ?? ''
      const list = groups.get(group)
      if (list) list.push(option)
      else groups.set(group, [option])
    }
    return Array.from(groups.entries())
  }, [filteredBySearch])

  if (!options || options.length === 0) return null

  return (
    <Stack useFlexGap gap={1.5}>
      <TextField
        fullWidth
        variant="outlined"
        size="small"
        placeholder={t('Search for a quality...')}
        value={searchText}
        onChange={e => setSearchText(e.target.value)}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: `${colors.grayPrimary} !important` }} />
              </InputAdornment>
            ),
            endAdornment: searchText ? (
              <InputAdornment position="end">
                <Button
                  id="btnClearQualitySearch"
                  aria-label={t('Clear search button')}
                  onClick={() => setSearchText('')}>
                  <Close />
                </Button>
              </InputAdornment>
            ) : null,
          },
        }}
      />
      {qualitiesError?.message && <ErrorText>{qualitiesError.message as string}</ErrorText>}
      {groupedByValue.map(([groupName, qualities]) => (
        <Stack key={groupName} useFlexGap gap={1}>
          <Typography variant="h3" sx={groupHeaderSx}>
            {groupName ? `${t('Value')}: ${groupName}` : ''}
          </Typography>
          {qualities.map(quality => {
            const isSelected = selectedIds.has(quality.id)
            return (
              <Stack
                key={quality.id}
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                width="100%">
                <Typography component="span" fontWeight={isSelected ? 600 : 400} variant="body1">
                  {quality.name}
                </Typography>
                <Checkbox
                  checked={selectedIds.has(quality.id)}
                  onChange={(_e, checked) => handleQualityToggle(quality, checked)}
                  id={`checkbox-quality-${quality.id}`}
                />
              </Stack>
            )
          })}
        </Stack>
      ))}
    </Stack>
  )
}

export default MobileRecognitionQualitiesView
